import { useEffect, useState, useCallback } from 'react';
import { useLocation } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Button } from './ui/button';
import { MobileDataVoiceInput, parseMobileBalances, EMPTY_BALANCES } from './MobileDataVoiceInput';
import { toast } from 'sonner';
import { Smartphone } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Nigerian balance-check USSD codes (network-specific data codes + unified
// airtime). Source: confirmed common 2026 codes; *323# (data) / *310#
// (airtime) also work network-wide.
export const BALANCE_CODES = [
  { net: 'MTN', airtime: '*310#', data: '*131*4#' },
  { net: 'Glo', airtime: '*310#', data: '*127*0#' },
  { net: 'Airtel', airtime: '*310#', data: '*140#' },
];

export const BalanceCheckCodes = ({ className = '' }) => (
  <div className={`rounded-lg border border-forest-100 bg-forest-50/60 p-3 ${className}`} data-testid="ussd-codes">
    <p className="text-[11px] font-semibold uppercase tracking-wide text-forest-700 mb-2">
      Check your balance (dial on the phone)
    </p>
    <div className="grid grid-cols-3 gap-2 text-[11px]">
      {BALANCE_CODES.map((c) => (
        <div key={c.net} className="rounded-md bg-white border border-forest-100 px-2 py-1.5">
          <p className="font-bold text-forest-800">{c.net}</p>
          <p className="text-muted-foreground">Data: <span className="font-mono text-foreground">{c.data}</span></p>
          <p className="text-muted-foreground">Voice: <span className="font-mono text-foreground">{c.airtime}</span></p>
        </div>
      ))}
    </div>
    <p className="text-[10px] text-muted-foreground mt-1.5">Tip: <span className="font-mono">*323#</span> (data) / <span className="font-mono">*310#</span> (airtime) work on all networks.</p>
  </div>
);

/**
 * Full-screen gate for Branch Managers: once they have clocked in, they cannot
 * use the rest of the app until they submit their MORNING Data (MB) + Voice (₦)
 * balances. Mounted globally inside StaffLayout. No-op for every other role.
 */
export const MobileBalanceGate = () => {
  const { user, getAuthHeaders } = useAuth();
  const location = useLocation();
  const [status, setStatus] = useState(null);
  const [vals, setVals] = useState(EMPTY_BALANCES);
  const [submitting, setSubmitting] = useState(false);

  // Active for any branch staff (server decides via gate-status.active).
  const isBranchStaff = !!user && !['customer', 'investor'].includes(user?.role) && !!user?.branch_id;

  const fetchStatus = useCallback(async () => {
    if (!isBranchStaff) return;
    try {
      const res = await fetch(`${API}/mobile-balances/gate-status`, { headers: getAuthHeaders() });
      if (res.ok) setStatus(await res.json());
    } catch (e) {
      /* network — keep previous status */
    }
  }, [isBranchStaff, getAuthHeaders]);

  useEffect(() => {
    fetchStatus();
  }, [fetchStatus, location.pathname]);

  // Re-check after clock-in (My Attendance dispatches this) + periodic safety net.
  useEffect(() => {
    if (!isBranchStaff) return undefined;
    const handler = () => fetchStatus();
    window.addEventListener('mb:refresh', handler);
    const id = setInterval(fetchStatus, 45000);
    return () => {
      window.removeEventListener('mb:refresh', handler);
      clearInterval(id);
    };
  }, [isBranchStaff, fetchStatus]);

  const submit = async () => {
    const { ok, parsed, error } = parseMobileBalances(vals);
    if (!ok) { toast.error(error); return; }
    setSubmitting(true);
    try {
      const res = await fetch(`${API}/mobile-balances/morning`, {
        method: 'POST',
        headers: { ...getAuthHeaders(), 'Content-Type': 'application/json' },
        body: JSON.stringify(parsed),
      });
      if (res.ok) {
        toast.success('Morning balances submitted. Have a great day!');
        setVals(EMPTY_BALANCES);
        window.dispatchEvent(new CustomEvent('mb:refresh'));
        await fetchStatus();
      } else {
        const err = await res.json().catch(() => ({}));
        toast.error(err.detail || 'Failed to submit balances');
      }
    } catch (e) {
      toast.error('Network error — please try again');
    } finally {
      setSubmitting(false);
    }
  };

  const setV = (k, v) => setVals((prev) => ({ ...prev, [k]: v }));

  if (!isBranchStaff || !status?.needs_morning) return null;
  const p1 = status.provider1_name || 'Provider 1';
  const p2 = status.provider2_name || 'Provider 2';

  return (
    <div
      className="fixed inset-0 z-[100000] flex items-center justify-center bg-forest-950/80 backdrop-blur-sm p-4"
      data-testid="mobile-balance-gate"
    >
      <div className="w-full max-w-lg rounded-2xl bg-white shadow-2xl border border-forest-200 overflow-hidden max-h-[92vh] overflow-y-auto">
        <div className="bg-forest-700 px-6 py-5 text-white">
          <div className="flex items-center gap-3">
            <div className="w-11 h-11 rounded-xl bg-white/15 flex items-center justify-center">
              <Smartphone className="w-6 h-6" />
            </div>
            <div>
              <h2 className="text-lg font-bold font-heading leading-tight">Morning Mobile Balances</h2>
              <p className="text-xs text-forest-100">Required before you can continue</p>
            </div>
          </div>
        </div>

        <div className="px-6 py-5 space-y-4">
          <p className="text-sm text-muted-foreground">
            Welcome, <span className="font-semibold text-foreground">{status.staff_name}</span>
            {status.branch_name ? <> — {status.branch_name}</> : null}. Record your current Data &amp;
            Voice balances for both providers to start the day.
          </p>

          <BalanceCheckCodes />

          {[{ key: 'p1', name: p1 }, { key: 'p2', name: p2 }].map(({ key, name }) => (
            <MobileDataVoiceInput key={key} prefix={key} name={name} vals={vals} setV={setV} testid="gate" />
          ))}

          <Button
            onClick={submit} disabled={submitting}
            className="w-full bg-forest-700 hover:bg-forest-800"
            data-testid="gate-submit-btn"
          >
            {submitting ? 'Submitting…' : 'Submit & Continue'}
          </Button>
          <p className="text-[11px] text-center text-muted-foreground">
            Entry is timestamped automatically. You cannot use the app until this is submitted.
          </p>
        </div>
      </div>
    </div>
  );
};

export default MobileBalanceGate;
